import { useMemo } from "react"
import { useAccessWindowIndex } from "~/features/entitlements/AccessWindowContext"
import { GetEbookLibraryDocument, type LibrarySortType } from "~/graphql/generated/graphql"
import { reportGaps } from "~/shared/lib/schemas"
import { logLibraryEdges } from "../cardTelemetry"
import { isEbookEdge } from "../guards"
import { toEbookCard } from "../projections"
import { useLibraryQuery } from "./useLibraryQuery"
import type { LibraryListResult } from "./useStudyLibrary"

export const useEbookLibrary = (sortBy: LibrarySortType): LibraryListResult => {
  const query = useLibraryQuery("ebook", GetEbookLibraryDocument, sortBy)

  /**
   * POC. The LIN comes off the matched commerce seat rather than `Ebook.lin`
   * itself, because that field exists only on this branch's schema — selecting
   * it against a deployed graph that lacks it fails the whole library request,
   * not just this section.
   */
  const { linFor } = useAccessWindowIndex()

  const edges = useMemo(
    () => (query.data?.pages ?? []).flatMap(page => page.user?.libraryV2?.edges ?? []).filter(isEbookEdge),
    [query.data?.pages],
  )

  const assets = useMemo(() => edges.map(edge => edge.node.asset), [edges])

  const items = useMemo(() => {
    logLibraryEdges("ebooks", edges)

    const cards = assets.map(asset => toEbookCard(asset, linFor))

    reportGaps("ebooks", cards)

    return cards
  }, [edges, assets, linFor])

  return {
    items,
    isLoading: query.isLoading,
    isError: query.isError,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
  }
}
